import { useEffect, useState } from "react";
import { Sparkles, Building2 } from "lucide-react";
import { http, money, notify } from "../api";
import { InlineEmiCalc } from "./PolicyFinder";

/**
 * Smart Policy Suggest — Lead Detail widget.
 * Auto-resolves the customer's employer via /companies/search, pulls its
 * category + linked bank, then queries /policies/search filtered by
 * (category, loan_type, and optionally bank). Renders the top 3 matches
 * as compact cards with an inline EMI calculator seeded from the lead.
 */
export default function SmartPolicySuggest({ lead }) {
  const [company, setCompany] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // policy_id

  const employer = (lead?.company || "").trim();
  const loanType = lead?.loan_type || "";
  const leadAmount = Number(lead?.loan_amount) || 500000;

  useEffect(() => {
    if (!employer) { setCompany(null); setPolicies([]); return; }
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // 1. Resolve company (fuzzy match)
        const cr = await http.get(`/companies/search?q=${encodeURIComponent(employer)}&limit=5`);
        const list = cr.data.companies || [];
        const exact = list.find((c) => c.name.toLowerCase() === employer.toLowerCase());
        const picked = exact || list[0] || null;
        if (cancelled) return;
        setCompany(picked);

        // 2. Suggest policies. Category comes from the company's lender-specific
        //    mappings; if the lead hasn't picked a lender yet we cast a wider
        //    net across every configured category for this company.
        const mappings = (picked?.mappings || []).filter(
          (m) => !loanType || (m.product_name || "").toLowerCase().includes(loanType.toLowerCase()) || loanType.toLowerCase().includes((m.product_name || "").toLowerCase())
        );
        const categories = [...new Set(mappings.map((m) => m.category).filter(Boolean))];

        let combined = [];
        if (!picked || categories.length === 0) {
          // No mapping → run one unrestricted policy search on loan_type.
          const params = new URLSearchParams();
          if (loanType) params.set("loan_type", loanType);
          params.set("limit", "20");
          const pr = await http.get(`/policies/search?${params.toString()}`);
          combined = pr.data.policies || [];
        } else {
          const seen = new Set();
          for (const cat of categories) {
            const params = new URLSearchParams();
            params.set("category", cat);
            if (loanType) params.set("loan_type", loanType);
            params.set("limit", "20");
            const pr = await http.get(`/policies/search?${params.toString()}`);
            for (const p of pr.data.policies || []) {
              if (seen.has(p.policy_id)) continue;
              seen.add(p.policy_id);
              combined.push({ ...p, matched_category: cat });
            }
          }
        }
        if (cancelled) return;
        setPolicies(combined.slice(0, 3));
      } catch (err) {
        if (!cancelled) notify.error(err, "Could not suggest policies");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [employer, loanType]);

  if (!employer) {
    return (
      <section className="panel" data-testid="smart-policy-suggest-panel">
        <div className="section-title">
          <h3><Sparkles size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} /> Smart policy suggest</h3>
        </div>
        <p className="muted" data-testid="smart-policy-suggest-noemp" style={{ padding: "8px 0" }}>
          Add the customer's employer to see matching bank policies.
        </p>
      </section>
    );
  }

  return (
    <section className="panel" data-testid="smart-policy-suggest-panel">
      <div className="section-title">
        <h3><Sparkles size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} /> Smart policy suggest</h3>
        {company && (
          <span className="muted" data-testid="smart-policy-suggest-company" style={{ fontSize: 12 }}>
            <Building2 size={12} style={{ verticalAlign: "-2px" }} /> {company.name}
            {(company.mappings || []).length > 0 ? ` · ${(company.mappings || []).length} lender mapping${(company.mappings || []).length === 1 ? "" : "s"}` : ""}
          </span>
        )}
      </div>

      {loading && (
        <p className="muted" data-testid="smart-policy-suggest-loading" style={{ padding: "8px 0" }}>
          Matching policies…
        </p>
      )}

      {!loading && !company && (
        <p className="muted" data-testid="smart-policy-suggest-nomatch-company" style={{ padding: "8px 0" }}>
          Employer "{employer}" isn't in the companies master yet — ask Admin to add it.
        </p>
      )}

      {!loading && company && policies.length === 0 && (
        <p className="muted" data-testid="smart-policy-suggest-nopolicies" style={{ padding: "8px 0" }}>
          No active policies match {loanType ? `${loanType}` : "this profile"} for {company.name}'s lender mappings. Widen the loan type or check with Admin.
        </p>
      )}

      {!loading && policies.length > 0 && (
        <div
          data-testid="smart-policy-suggest-list"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginTop: 4 }}
        >
          {policies.map((p) => (
            <SuggestCard
              key={p.policy_id}
              policy={p}
              expanded={expanded === p.policy_id}
              onToggle={() => setExpanded(expanded === p.policy_id ? null : p.policy_id)}
              leadAmount={leadAmount}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SuggestCard({ policy, expanded, onToggle, leadAmount }) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    if (!expanded || docs) return;
    http.get(`/policies/${policy.policy_id}`)
      .then((r) => setDocs(r.data.required_documents || []))
      .catch(() => setDocs([]));
  }, [expanded, docs, policy.policy_id]);

  const midRoi = ((Number(policy.roi_min) + Number(policy.roi_max)) / 2) || Number(policy.roi_min) || 12;
  const seedTenure = Math.max(Number(policy.tenure_min_months) || 12, 12);
  const seedPrincipal = Math.min(leadAmount, Number(policy.max_loan_amount) || leadAmount);

  return (
    <div
      data-testid={`smart-policy-card-${policy.policy_id}`}
      style={{
        border: "1px solid var(--border)", background: "var(--surface)",
        padding: 12, display: "flex", flexDirection: "column", gap: 6,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <strong style={{ fontSize: 13 }}>{policy.bank_name}</strong>
          <div className="muted" style={{ fontSize: 11 }}>{policy.product_name}</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 11 }}>
        <div><span className="muted">ROI</span><br /><strong>{policy.roi_min}%–{policy.roi_max}%</strong></div>
        <div><span className="muted">Max loan</span><br /><strong>{policy.max_loan_amount ? money(policy.max_loan_amount) : "—"}</strong></div>
        <div><span className="muted">Tenure</span><br /><strong>{policy.tenure_min_months}–{policy.tenure_max_months} mo</strong></div>
        <div><span className="muted">Min income</span><br /><strong>{policy.min_income ? money(policy.min_income) : "—"}</strong></div>
      </div>
      <button
        type="button"
        className="outline-button"
        data-testid={`smart-policy-card-toggle-${policy.policy_id}`}
        onClick={onToggle}
        style={{ fontSize: 11, padding: "4px 8px", alignSelf: "flex-start" }}
      >
        {expanded ? "Hide details" : "Docs & EMI"}
      </button>
      {expanded && (
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 8, marginTop: 4 }}>
          <strong style={{ fontSize: 11 }}>Required docs</strong>
          {docs === null && <p className="muted" style={{ fontSize: 11 }}>Loading…</p>}
          {docs && docs.length === 0 && (
            <p className="muted" style={{ fontSize: 11 }} data-testid={`smart-policy-docs-empty-${policy.policy_id}`}>
              None configured on this bank yet.
            </p>
          )}
          {docs && docs.length > 0 && (
            <ul style={{ margin: "4px 0 8px 16px", fontSize: 11 }} data-testid={`smart-policy-docs-${policy.policy_id}`}>
              {docs.map((d, i) => (
                <li key={i}>{d.name}{d.mandatory ? " *" : ""}{d.notes ? <span className="muted"> — {d.notes}</span> : null}</li>
              ))}
            </ul>
          )}
          <InlineEmiCalc
            initialPrincipal={seedPrincipal}
            initialRoi={midRoi}
            initialTenure={seedTenure}
            testIdPrefix={`smart-policy-emi-${policy.policy_id}`}
          />
        </div>
      )}
    </div>
  );
}
